import { useLazyQuery, useQuery } from '@apollo/client'

import { GQL_ZONES_FOR_GEO_POINT } from '../api/graphql'

import type { DeliveryZone } from '../types'

interface GeoPointInput {
  latitude?: number | null
  longitude?: number | null
}

interface ZonesForGeoPointQuery {
  zonesForGeoPoint: DeliveryZone[]
}

interface ZonesForGeoPointVariables {
  point: {
    latitude: number | null | undefined
    longitude: number | null | undefined
  }
  customerType?: 'customer' | 'business'
}

interface UseDeliveryZonesForGeoPointProps {
  point?: GeoPointInput | null
  customerType?: 'customer' | 'business'
  skip?: boolean
}

/**
 * Хук для получения зон доставки по координатам
 * Аналог useFetchDeliveryZonesForGeoPoint из приложения
 */
export function useDeliveryZonesForGeoPoint ({
  point,
  customerType = 'customer',
  skip: externalSkip = false,
}: UseDeliveryZonesForGeoPointProps) {
  const hasValidPoint = Boolean(point?.latitude && point?.longitude)
  const skip = externalSkip || !hasValidPoint

  const { data, loading, error, refetch } = useQuery<
    ZonesForGeoPointQuery,
    ZonesForGeoPointVariables
  >(GQL_ZONES_FOR_GEO_POINT, {
    variables: {
      point: {
        latitude: point?.latitude,
        longitude: point?.longitude,
      },
      customerType,
    },
    skip,
    fetchPolicy: 'network-only',
  })

  const deliveryZones = data?.zonesForGeoPoint ?? []

  return {
    deliveryZones,
    data,
    loading,
    error,
    refetch,
  }
}

interface UseLazyDeliveryZonesForGeoPointProps {
  customerType?: 'customer' | 'business'
}

/**
 * Ленивый хук для получения зон доставки по координатам
 * Аналог useLazyFetchDeliveryZonesForGeoPoint из приложения
 */
export function useLazyDeliveryZonesForGeoPoint ({
  customerType = 'customer',
}: UseLazyDeliveryZonesForGeoPointProps = {}) {
  const [ fetchZones, { data, loading, error, called } ] = useLazyQuery<
    ZonesForGeoPointQuery,
    ZonesForGeoPointVariables
  >(GQL_ZONES_FOR_GEO_POINT, {
    fetchPolicy: 'network-only',
  })

  const getZonesForGeoPoint = async (point: GeoPointInput) => {
    return fetchZones({
      variables: {
        point: {
          latitude: point.latitude,
          longitude: point.longitude,
        },
        customerType,
      },
    })
  }

  const deliveryZones = data?.zonesForGeoPoint ?? []

  return {
    getZonesForGeoPoint,
    deliveryZones,
    data,
    loading,
    error,
    called,
  }
}
